// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Tabs, Tab } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnMount,
  useOnChange,
  useOnPendingActionFinish
} from '../../../../../common/hooks';
import { AlertSeverity } from '../../../../../common/providers';
import { AppRoute } from '../../../../app';
import { EmptyState, LoadingWrapper, Panel, TabPanel } from '../../../../../common/components';
import { selectPendingActions } from '../../../../../common/state/pending';
import ProductDetailsTab from './partials/ProductDetailsTab';
import ProductChangesTab from './partials/ProductChangesTab';
import { selectProductDetails, selectProductChanges, selectProductChangesNext } from '../../../state/productSelectors';
import { createProductChange, deleteProductChange, fetchProduct, fetchProductChange, selectProduct, deselectProduct, fetchNextProductChange } from '../../../state/productActions';
import { AsyncUtils } from '../../../../../common/utils';
import { CREATE_PRODUCT_CHANGE, DELETE_PRODUCT_CHANGE } from '../../../state/productActionTypes';

const TabIndex = {
  Details: 0,
  Changes: 1,
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  fullHeight: {
    flex: '1',
    height: '100%',
  },
}));

function ProductDetailsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [tabIndex, setTabIndex] = useHistoryState('tabIndex', TabIndex.Details);
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const { productId } = useParams();
  const history = useHistory();
  const pendingActions = useSelector(selectPendingActions);
  const productDetails = useSelector(selectProductDetails);
  const productChanges = useSelector(selectProductChanges);
  const productChangesNext = useSelector(selectProductChangesNext);
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  const [filterParams, setFilterParams] = useHistoryState('productChangeParams', {
    productIds: [ productId ],
    states: [ 'PENDING' ]
  });

  const [sortParams, setSortParams] = useHistoryState('productChangeSortParams', {
    sortBy: 'effective_date',
    sortOrder: 'asc'
  });

  useOnMount(() => {
    async function handleFetchData() {
      try {
        setLoading(true);
        if (!productDetails) {
          dispatch(selectProduct(productId));
          if (!productDetails) {
            await dispatch(fetchProduct(productId));
          }
        }
        await AsyncUtils.executeAll([
          dispatch(fetchProductChange(filterParams, sortParams, 1, 20)),
          dispatch(fetchProduct(productId)),
          dispatch(fetchNextProductChange(productId))
        ]);
      } catch (e) {
        showAlert({
          title: t('product.productDetailsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }
    handleFetchData();
  });

  useOnPendingActionFinish([DELETE_PRODUCT_CHANGE, CREATE_PRODUCT_CHANGE], () => {
    try {
      AsyncUtils.executeAll([
        
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  });

  const handleCreateProductChange = useCallback(
    async productChangeData => {
      try {
        setLoading(true);
        await dispatch(createProductChange(productChangeData));
        await dispatch(fetchProductChange(filterParams, sortParams, 1, 20));
        await dispatch(fetchNextProductChange(productId));
        showAlert({
          title: t('common.success'),
          message: t('product.productChangeCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('product.productChangeCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      } finally {
        setLoading(false);
      }
    },
    [dispatch, filterParams, showAlert, t, sortParams]
  );

  const handleCreateProductChangeError = useCallback(
    async error => {
        showAlert({
          title: t('common.error'),
          message: error,
          severity: AlertSeverity.Error,
        });
    },
    [showAlert, t]
  );

  const handleDeleteProductChange = useCallback(
    async productChangeId => {
      try {
        setLoading(true);
        await dispatch(deleteProductChange(productChangeId));
        await dispatch(fetchProductChange(filterParams, sortParams, 1, 20));
        await dispatch(fetchNextProductChange(productId));
        showAlert({
          title: t('common.success'),
          message: t('product.productChangeDeleteSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('product.productChangeDeleteError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      } finally {
        setLoading(false);
      }
    },
    [dispatch, showAlert, t, filterParams, sortParams]
  );

  const handleFilterChange = useCallback(
    params => {
      setFilterParams({
        productIds: [ productId ],
        states: params ? [ 'PENDING' ] : [ 'ALL' ]
      });
    },
    [setFilterParams, productId]
  );

  useOnChange(filterParams, async () => {
    await dispatch(fetchProductChange(filterParams, sortParams, 1, 20));
  });

  const handlePageChange = useCallback(
    async newPage => {
      await dispatch(fetchProductChange(filterParams, sortParams, newPage, 20));
    },
    [dispatch, filterParams, sortParams]
  );

  const handleSortChange = useCallback(
    params => {
      setSortParams(params);
    },
    [setSortParams]
  );

  useOnChange(sortParams, async () => {
    await dispatch(fetchProductChange(filterParams, sortParams, 1, 20));
  });

  const handleGoBack = useCallback(async () => {
    try {
      setLoading(true);
      dispatch(deselectProduct());
      await dispatch(fetchProductChange());
      history.push(_.defaultTo(history.location.state?.from, AppRoute.Products));
    } catch (e) {
      showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
      throw e; // Rethrow to catch later
    } finally {
      setLoading(false);
    }
  }, [dispatch, history, showAlert, t]);

  return (
    <Box id="ProductDetails-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel
          id="ProductDetails-panel"
          title={t('product.productDetails')}
          className={clsx(classes.panel, {
            [classes.fullHeight]: tabIndex !== TabIndex.Details,
          })}
        >
          {!productDetails ? (
            <EmptyState variant="unavailable" message={t('common.empty')} />
          ) : (
            <>
              <Tabs
                id="ProductDetails-tabs"
                className={classes.tabs}
                data-value={tabIndex}
                value={tabIndex}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event, index) => setTabIndex(index)}
              >
                <Tab
                  id="ProductDetails-detailsTab"
                  label={t('product.product')}
                  data-index={TabIndex.Details}
                />
                <Tab
                  id="ProductDetails-changesTab"
                  label={t('product.productChanges')}
                  data-index={TabIndex.Changes}
                />
              </Tabs>
              <Box id="ProductDetails-tabsContent" className={classes.fullHeight}>
                <TabPanel index={TabIndex.Details} currentIndex={tabIndex}>
                  <ProductDetailsTab
                    product={productDetails}
                    productChanges={productChangesNext}
                    onGoBack={handleGoBack}
                  />
                </TabPanel>
                <TabPanel index={TabIndex.Changes} currentIndex={tabIndex}>
                  <ProductChangesTab
                    product={productDetails}
                    productChanges={productChanges}
                    filterParam={filterParams.states.some(x => x === 'PENDING')}
                    sortParams={sortParams}
                    onDeleteProductChange={handleDeleteProductChange}
                    onCreateProductChange={handleCreateProductChange}
                    onCreateProductChangeError={handleCreateProductChangeError}
                    onFilterChange={handleFilterChange}
                    onSortChange={handleSortChange}
                    onPageChange={handlePageChange}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                  />
                </TabPanel>
              </Box>
            </>
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default ProductDetailsPage;
